import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { CalendarIntegrations } from '../components/CalendarIntegrations';
import { 
  Building2, 
  Save, 
  Loader2,
  Upload,
  CreditCard,
  FileText,
  Download,
  Mail,
  Eye,
  Copy,
  Check
} from 'lucide-react';
import { toast } from 'sonner';

export const BusinessSettingsPage = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [businessInfo, setBusinessInfo] = useState({
    company_name: '',
    address: '',
    city: '',
    postal_code: '',
    country: 'France',
    siret: '',
    tva_number: '',
    phone: '',
    email: '',
    website: '',
    logo_base64: null,
    bank_iban: '',
    bank_bic: '',
    payment_terms: 'Paiement à 30 jours',
    default_tva_rate: 20.0
  });
  const [signatureHtml, setSignatureHtml] = useState('');
  const [showSignaturePreview, setShowSignaturePreview] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchBusinessInfo();
  }, []);

  const fetchBusinessInfo = async () => {
    try {
      const response = await api.get('/business-info');
      if (response.data) {
        setBusinessInfo({ ...businessInfo, ...response.data });
        // Fetch signature if company name exists
        if (response.data.company_name) {
          fetchSignature();
        }
      }
    } catch (error) {
      console.error('Error fetching business info:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSignature = async () => {
    try {
      const response = await api.get('/business-info/signature');
      setSignatureHtml(response.data.html);
    } catch (error) {
      console.error('Error fetching signature:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/business-info', businessInfo);
      toast.success('Informations sauvegardées');
      // Refresh signature after save
      if (businessInfo.company_name) {
        fetchSignature();
      }
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadSignature = async () => {
    try {
      const response = await api.get('/business-info/signature/download', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'signature_email.html');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Signature téléchargée');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du téléchargement');
    }
  };

  const handleCopySignature = async () => {
    try {
      await navigator.clipboard.writeText(signatureHtml);
      setCopied(true);
      toast.success('Signature copiée dans le presse-papier');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Erreur lors de la copie');
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 1024 * 1024) {
      toast.error('Le logo ne doit pas dépasser 1 Mo');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setBusinessInfo({ ...businessInfo, logo_base64: event.target?.result });
    };
    reader.readAsDataURL(file);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#065F46]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="business-settings-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-outfit text-2xl md:text-3xl font-bold text-slate-900">Mon entreprise</h1>
          <p className="text-slate-500">Configurez vos informations pour les devis et factures</p>
        </div>
        <Button 
          onClick={handleSave}
          disabled={saving}
          className="bg-[#065F46] hover:bg-[#047857] text-white"
          data-testid="save-business-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Sauvegarder
        </Button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Calendar integrations - spans full width */}
        <div className="lg:col-span-2">
          <CalendarIntegrations />
        </div>

        {/* Company Information */}
        <Card className="border border-slate-200">
          <CardHeader>
            <CardTitle className="font-outfit text-lg flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[#065F46]" />
              Informations entreprise
            </CardTitle>
            <CardDescription>Ces informations apparaîtront sur vos documents</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Logo */}
            <div className="space-y-2">
              <Label>Logo</Label>
              <div className="flex items-center gap-4">
                {businessInfo.logo_base64 ? (
                  <img 
                    src={businessInfo.logo_base64} 
                    alt="Logo" 
                    className="w-16 h-16 object-contain border border-slate-200 rounded-lg"
                  />
                ) : (
                  <div className="w-16 h-16 border-2 border-dashed border-slate-200 rounded-lg flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-slate-300" />
                  </div>
                )}
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                    id="logo-upload"
                  />
                  <label htmlFor="logo-upload">
                    <Button type="button" variant="outline" size="sm" asChild>
                      <span className="cursor-pointer">
                        <Upload className="w-4 h-4 mr-1" /> Changer
                      </span>
                    </Button>
                  </label>
                  <p className="text-xs text-slate-400 mt-1">PNG, JPG max 1 Mo</p>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="company_name">Nom de l'entreprise</Label>
              <Input
                id="company_name"
                value={businessInfo.company_name}
                onChange={(e) => setBusinessInfo({ ...businessInfo, company_name: e.target.value })}
                placeholder="Ma Société SARL"
                data-testid="company-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">Adresse</Label>
              <Input
                id="address"
                value={businessInfo.address}
                onChange={(e) => setBusinessInfo({ ...businessInfo, address: e.target.value })}
                placeholder="123 Rue Example"
                data-testid="company-address-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="postal_code">Code postal</Label>
                <Input
                  id="postal_code"
                  value={businessInfo.postal_code}
                  onChange={(e) => setBusinessInfo({ ...businessInfo, postal_code: e.target.value })}
                  placeholder="75001"
                  data-testid="company-postal-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="city">Ville</Label>
                <Input
                  id="city"
                  value={businessInfo.city}
                  onChange={(e) => setBusinessInfo({ ...businessInfo, city: e.target.value })}
                  placeholder="Paris"
                  data-testid="company-city-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone">Téléphone</Label>
                <Input
                  id="phone"
                  value={businessInfo.phone}
                  onChange={(e) => setBusinessInfo({ ...businessInfo, phone: e.target.value })}
                  placeholder="01 23 45 67 89"
                  data-testid="company-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={businessInfo.email}
                  onChange={(e) => setBusinessInfo({ ...businessInfo, email: e.target.value })}
                  placeholder="contact@masociete.fr"
                  data-testid="company-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="website">Site internet</Label>
              <Input
                id="website"
                value={businessInfo.website}
                onChange={(e) => setBusinessInfo({ ...businessInfo, website: e.target.value })}
                placeholder="https://monsite.fr"
                data-testid="company-website-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="siret">SIRET</Label>
                <Input
                  id="siret"
                  value={businessInfo.siret}
                  onChange={(e) => setBusinessInfo({ ...businessInfo, siret: e.target.value })}
                  placeholder="123 456 789 00012"
                  data-testid="company-siret-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tva_number">N° TVA Intracommunautaire</Label>
                <Input
                  id="tva_number"
                  value={businessInfo.tva_number}
                  onChange={(e) => setBusinessInfo({ ...businessInfo, tva_number: e.target.value })}
                  placeholder="FR12345678901"
                  data-testid="company-tva-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Payment & Invoice Settings */}
        <div className="space-y-6">
          <Card className="border border-slate-200">
            <CardHeader>
              <CardTitle className="font-outfit text-lg flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-[#065F46]" />
                Coordonnées bancaires
              </CardTitle>
              <CardDescription>Pour les virements de vos clients</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="bank_iban">IBAN</Label>
                <Input
                  id="bank_iban"
                  value={businessInfo.bank_iban}
                  onChange={(e) => setBusinessInfo({ ...businessInfo, bank_iban: e.target.value })}
                  placeholder="FR76 1234 5678 9012 3456 7890 123"
                  className="font-mono"
                  data-testid="bank-iban-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bank_bic">BIC / SWIFT</Label>
                <Input
                  id="bank_bic"
                  value={businessInfo.bank_bic}
                  onChange={(e) => setBusinessInfo({ ...businessInfo, bank_bic: e.target.value })}
                  placeholder="BNPAFRPP"
                  className="font-mono"
                  data-testid="bank-bic-input"
                />
              </div>
            </CardContent>
          </Card>

          <Card className="border border-slate-200">
            <CardHeader>
              <CardTitle className="font-outfit text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#065F46]" />
                Paramètres des documents
              </CardTitle>
              <CardDescription>Valeurs par défaut pour vos devis et factures</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="payment_terms">Conditions de paiement</Label>
                <Textarea
                  id="payment_terms"
                  value={businessInfo.payment_terms}
                  onChange={(e) => setBusinessInfo({ ...businessInfo, payment_terms: e.target.value })}
                  placeholder="Paiement à 30 jours par virement bancaire"
                  rows={2}
                  data-testid="payment-terms-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="default_tva_rate">Taux de TVA par défaut (%)</Label>
                <Input
                  id="default_tva_rate"
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={businessInfo.default_tva_rate}
                  onChange={(e) => setBusinessInfo({ ...businessInfo, default_tva_rate: parseFloat(e.target.value) || 20 })}
                  className="w-32"
                  data-testid="default-tva-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Email Signature */}
          <Card className="border border-slate-200">
            <CardHeader>
              <CardTitle className="font-outfit text-lg flex items-center gap-2">
                <Mail className="w-5 h-5 text-[#065F46]" />
                Signature électronique
              </CardTitle>
              <CardDescription>Ajoutée automatiquement à vos devis et factures, téléchargeable pour vos emails</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {businessInfo.company_name ? (
                <>
                  {/* Preview */}
                  <div className="border border-slate-200 rounded-lg p-4 bg-white">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-slate-600">Aperçu</span>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setShowSignaturePreview(!showSignaturePreview)}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        {showSignaturePreview ? 'Masquer' : 'Voir'}
                      </Button>
                    </div>
                    {showSignaturePreview && signatureHtml && (
                      <div 
                        className="border-t pt-4"
                        dangerouslySetInnerHTML={{ __html: signatureHtml }}
                      />
                    )}
                    {!showSignaturePreview && (
                      <div className="text-sm text-slate-500 italic">
                        Cliquez sur "Voir" pour prévisualiser votre signature
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <Button 
                      variant="outline"
                      onClick={handleCopySignature}
                      disabled={!signatureHtml}
                      className="flex-1"
                      data-testid="copy-signature-btn"
                    >
                      {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                      {copied ? 'Copié !' : 'Copier HTML'}
                    </Button>
                    <Button 
                      onClick={handleDownloadSignature}
                      className="flex-1 bg-[#065F46] hover:bg-[#047857] text-white"
                      data-testid="download-signature-btn"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Télécharger
                    </Button>
                  </div>

                  <p className="text-xs text-slate-400">
                    💡 Astuce : Collez le fichier HTML dans les paramètres de signature de votre client email (Gmail, Outlook, etc.)
                  </p>
                </>
              ) : (
                <div className="text-center py-6">
                  <Mail className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-sm text-slate-500">
                    Remplissez les informations de votre entreprise pour générer votre signature électronique
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
